import {
  BadgeImage,
  type BadgeImageSize,
} from "@/components/badges/badge-image"
import type { RankBlazonInput } from "@/types/rank"

import { rankTintGradient } from "./rank-blazon-glyphs"

/**
 * OKS-539 — Frame-less rank blazon (image-with-default).
 *
 * The single rank-side wrapper over the generalized OKS-538 `<BadgeImage>`
 * primitive. It maps a `RankBlazonInput` (`imageUrl` + ladder
 * `tier`/`tierCount` + real `name`) onto `<BadgeImage>`:
 *
 *   - With an uploaded blazon → renders it raw (`object-cover`, rounded,
 *     no shield/ring/gradient frame).
 *   - Without one → a plain-color square tinted by the rank's LADDER
 *     POSITION via `rankTintGradient` (the same `blazonFor` tier palette
 *     the old `<RankPatch>` shield used), so the placeholder stays
 *     consistent with the identity system (PRD Q1). NEVER the old
 *     generated shield, never a broken-image glyph (PRD Q3).
 *
 * Consumed by all 4 rank surfaces (rank-up modal, momentum-avatar corner,
 * welcome header, profile crest), replacing the generated `<RankPatch>`.
 * Centralizing the position→tint mapping here keeps the surfaces tidy and
 * gives ONE test point for the tint logic.
 *
 * # Accessibility (UX Spec §6)
 *
 * `decorative` ⇒ the surrounding UI already names the rank (avatar corner,
 * cards/headers that label the tier), so the `<img>` is `alt=""` and the
 * tinted default is `aria-hidden` — rank identity is carried by the
 * adjacent text, never colour alone.
 */
export type RankBlazonSize = "hero" | "crest" | "inline" | "corner"

// RankBlazon size → BadgeImage size token.
const SIZE_MAP: Record<RankBlazonSize, BadgeImageSize> = {
  hero: "modal", // rank-up modal — 96px
  crest: "preview", // profile card crest — 48px
  inline: "inline", // welcome header — 40px
  corner: "corner", // avatar bottom-right pin — 28px
}

export interface RankBlazonProps {
  /** `{ imageUrl, tier, tierCount, name }` for the rank to render. */
  input: RankBlazonInput
  /** `hero` modal | `crest` profile | `inline` header | `corner` avatar. */
  size?: RankBlazonSize
  /** `alt=""` + `aria-hidden` default when the surrounding text names the rank. */
  decorative?: boolean
  className?: string
}

export function RankBlazon({
  input,
  size = "crest",
  decorative = false,
  className,
}: RankBlazonProps) {
  return (
    <BadgeImage
      imageUrl={input.imageUrl}
      defaultStyle={{
        background: rankTintGradient(input.tier, input.tierCount),
      }}
      name={input.name}
      size={SIZE_MAP[size]}
      decorative={decorative}
      className={className}
    />
  )
}

export default RankBlazon
